// Geometry for the radial order menu (spec Addendum A: "right-click a unit = radial menu centered on the
// unit: wedges for the legal order cards, grouped and coloured by family").
//
// Pure math and naming, no React and no store: the gesture controller (src/map/gestures.ts) hit-tests
// against the same wedge table the menu draws, so what the pointer is over and what is highlighted can
// never disagree.

import { CARDS, FAMILY_META } from '../../sim/deck';
import type { CardDef, CardId, Family } from '../../sim/types';

/**
 * The arming distance, in screen pixels, measured from where the pointer was when the menu opened.
 *
 * It used to be measured from the menu's hub, which is not the same point: a unit near a rail has its
 * menu pulled inboard to fit, so the cursor started that far from the hub and a wedge was already lit
 * and already chooseable. A right-click and release - no movement at all - issued an order. The gesture
 * now asks for real travel: nothing lights, and nothing can be chosen, until the pointer has moved this
 * far from where it started.
 */
export const RADIAL_ARM = 24;
/** Wedge geometry, in screen pixels from the unit symbol. */
export const RADIAL_INNER = 36;
export const RADIAL_OUTER = 154;
/** The thin family band just outside the wedges, which carries the family colour. */
export const RADIAL_BAND_IN = 158;
export const RADIAL_BAND_OUT = 165;
/** Half-extent of the SVG box at full size; the viewBox is centred on the unit. */
export const RADIAL_HALF = 190;
/**
 * How far the menu may shrink when the unit is too close to a rail for the full one to fit.
 *
 * Reflowing (a smaller menu, drawn where the unit is) beats clamping (a full-size menu, dragged bodily
 * inboard): the same gesture works, the leader line stays short, and the cursor never starts a long way
 * from the hub. Below this the wedge labels stop being readable, so what is left is clamped.
 */
export const RADIAL_MIN_SCALE = 0.6;

/**
 * The scale and centre for a menu opened on `anchor`, inside a box with the rails' insets taken off.
 *
 * Full size and centred on the unit whenever it fits; otherwise shrunk to what the tightest side allows
 * (down to `RADIAL_MIN_SCALE`) and only then nudged inboard for whatever still does not fit.
 */
export function radialFit(
  anchor: { x: number; y: number },
  box: { width: number; height: number },
  insets: { left: number; right: number; top: number; bottom: number },
): { center: { x: number; y: number }; scale: number } {
  // The hint line sits under the menu, so the bottom wants a little more room than the top.
  const HINT = 24;
  const left = anchor.x - insets.left;
  const right = box.width - insets.right - anchor.x;
  const top = anchor.y - insets.top;
  const bottom = box.height - insets.bottom - anchor.y - HINT;
  const room = Math.min(left, right, top, bottom);
  const want = RADIAL_HALF - 10;
  const scale = Math.max(RADIAL_MIN_SCALE, Math.min(1, room / want));

  const pad = want * scale;
  const clamp = (v: number, lo: number, hi: number): number =>
    (lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v)));
  return {
    center: {
      x: clamp(anchor.x, insets.left + pad, box.width - insets.right - pad),
      y: clamp(anchor.y, insets.top + pad, box.height - insets.bottom - pad - HINT),
    },
    scale,
  };
}

/**
 * Where each part of a wedge sits along its own radius. A wedge is 22 degrees wide, which is far too
 * narrow for horizontal text, so the label runs outward along the radius instead; these three radii are
 * spaced so the longest label ("FIRE MISSION") clears both the shape mark inboard of it and the issue
 * cost outboard of it.
 */
export const RADIAL_MARK_R = 48;
export const RADIAL_LABEL_R = 94;
export const RADIAL_COST_R = 145;

/** A hairline of empty angle either side of a wedge, so the arcs read as separate cards. */
const PAD_RAD = 0.012;
/** Wedge zero is straight up; the deck's family order then runs clockwise. */
const START = -Math.PI / 2;

/**
 * Every card except the FRAGO (fragmentary order), which stays in the deck: it is a free-text panel,
 * not a map gesture (spec §6). data/deck.json already lists the cards grouped by family, so taking them
 * in order gives each family one contiguous arc.
 */
export const RADIAL_CARDS: CardDef[] = CARDS.filter((c) => c.id !== 'frago');

/** One or two words per wedge: a card's full deck label ("Fire Mission (HE)") does not fit an arc. */
export const RADIAL_LABEL: Record<CardId, string> = {
  move: 'MOVE',
  attack: 'ATTACK',
  defend: 'DEFEND',
  screen: 'SCREEN',
  withdraw: 'WITHDRAW',
  fireMission: 'FIRE MISSION',
  smoke: 'SMOKE',
  suppress: 'SUPPRESS',
  launchUas: 'LAUNCH UAS',
  recon: 'RECON',
  observe: 'OBSERVE NAI',
  resupply: 'RESUPPLY',
  casevac: 'CASEVAC',
  cancel: 'CANCEL',
  priorityFires: 'PRI FIRES',
  mainEffort: 'MAIN EFFORT',
  frago: 'FRAGO',
};

export interface Wedge {
  card: CardDef;
  /** Index into `RADIAL_CARDS`; also the wedge's slot, clockwise from straight up. */
  index: number;
  /** Radians, screen convention (x east, y south). `start`/`end` already carry the hairline padding. */
  start: number;
  end: number;
  mid: number;
  color: string;
  shape: FamilyShape;
}

export type FamilyShape = (typeof FAMILY_META)[Family]['shape'];

const STEP = (Math.PI * 2) / RADIAL_CARDS.length;

/** The wedge table: one entry per card, equal angular share, in deck order clockwise from the top. */
export function radialWedges(): Wedge[] {
  return RADIAL_CARDS.map((card, index) => {
    const a0 = START + index * STEP;
    const meta = FAMILY_META[card.family];
    return {
      card,
      index,
      start: a0 + PAD_RAD,
      end: a0 + STEP - PAD_RAD,
      mid: a0 + STEP / 2,
      color: meta.color,
      shape: meta.shape,
    };
  });
}

export interface FamilyArc { family: Family; label: string; start: number; end: number; mid: number; color: string }

const FAMILY_LABEL: Record<Family, string> = {
  maneuver: 'MANEUVER', fires: 'FIRES', isr: 'ISR', sustain: 'SUSTAIN', command: 'COMMAND',
};

/** One contiguous arc per family, for the band outside the wedges. */
export function radialFamilyArcs(): FamilyArc[] {
  const arcs: FamilyArc[] = [];
  RADIAL_CARDS.forEach((card, index) => {
    const a0 = START + index * STEP;
    const last = arcs[arcs.length - 1];
    if (last && last.family === card.family) {
      last.end = a0 + STEP;
      last.mid = (last.start + last.end) / 2;
      return;
    }
    arcs.push({
      family: card.family,
      label: FAMILY_LABEL[card.family],
      start: a0,
      end: a0 + STEP,
      mid: a0 + STEP / 2,
      color: FAMILY_META[card.family].color,
    });
  });
  return arcs;
}

/**
 * Which wedge slot an offset from the menu centre falls in. Angle alone selects (a drag only has to get
 * the direction right); the caller checks `RADIAL_ARM` for whether the choice is armed.
 */
export function wedgeIndexAt(dx: number, dy: number): number {
  const rel = (((Math.atan2(dy, dx) - START) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  return Math.min(RADIAL_CARDS.length - 1, Math.floor(rel / STEP));
}

/** A donut sector as an SVG path, centred on (0, 0). */
export function arcPath(r0: number, r1: number, a0: number, a1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p = (r: number, a: number): string => `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;
  return `M${p(r1, a0)} A${r1},${r1} 0 ${large} 1 ${p(r1, a1)} L${p(r0, a1)} A${r0},${r0} 0 ${large} 0 ${p(r0, a0)} Z`;
}

/** An arc with no thickness, for the family band's text path and its hairline. */
export function ringPath(r: number, a0: number, a1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p = (a: number): string => `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;
  return `M${p(a0)} A${r},${r} 0 ${large} 1 ${p(a1)}`;
}

/** Degrees for an SVG `rotate()`, plus whether the label would come out upside down at that angle. */
export function labelRotation(mid: number): { deg: number; flip: boolean } {
  const deg = (mid * 180) / Math.PI;
  const norm = ((deg % 360) + 360) % 360;
  return { deg, flip: norm > 90 && norm < 270 };
}
